import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { todayIso } from "@/lib/entries";
import { XCircle, CalendarClock } from "lucide-react";

const NEXT_FOLLOWUP_REASON = "Next Follow-up";
const NEXT_APPT_REASON = "Next appointment given";

export default function CloseFollowupDialog({ open, onOpenChange, entry, onClosed }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [nextNote, setNextNote] = useState("");
  const [saving, setSaving] = useState(false);

  const scheduleNext = useMemo(
    () => reason.trim().toLowerCase() === NEXT_FOLLOWUP_REASON.toLowerCase(),
    [reason]
  );

  useEffect(() => {
    if (!open) return;
    setReason(NEXT_APPT_REASON); setNote(""); setNextDate(""); setNextNote("");
    (async () => {
      try {
        const { data } = await api.get("/settings");
        // Always show "Next appointment given" as the first, sticky option.
        const configured = (data.close_reasons || []).filter(
          (r) => r.trim().toLowerCase() !== NEXT_APPT_REASON.toLowerCase()
        );
        setReasons([NEXT_APPT_REASON, ...configured]);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [open]);

  const submit = async () => {
    if (!reason) { toast.error("Please pick a closing reason"); return; }
    if (scheduleNext && !nextDate) { toast.error("Please pick a date for the next follow-up"); return; }
    setSaving(true);
    try {
      await api.post(`/entries/${entry.id}/close`, { reason, note });
      let createdNext = false;
      if (scheduleNext && nextDate) {
        try {
          // Record the scheduled next-follow-up date on the CLOSED entry so the
          // Follow-up sheet's Completed tab can surface it.
          await api.put(`/entries/${entry.id}`, { next_date: nextDate });
          await api.post("/entries", {
            patient_name: entry.patient_name,
            entry_date: todayIso(),
            status: entry.status || "ongoing_treatment",
            notes: nextNote || "",
            scheduled_date: nextDate,
            followup_date: nextDate,
            // Tag as originating from the Follow-up sheet so it stays hidden from
            // Daily Log and shows only under Pending in the Follow-ups sheet.
            source: "followup_sheet",
          });
          createdNext = true;
        } catch (err) {
          toast.error("Closed, but couldn't create next follow-up: " + formatApiError(err));
        }
      }
      toast.success(createdNext ? "Closed · next follow-up scheduled" : "Follow-up closed");
      onClosed?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="close-followup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <XCircle size={18} className="text-slate-700" /> Close follow-up
          </DialogTitle>
          <DialogDescription>
            Closing for <span className="font-medium text-slate-800">{entry?.patient_name}</span>. Pick a reason below.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="close-reason-select"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`close-reason-${r}`}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="Anything to remember about the closure?"
                      data-testid="close-note-input" />
          </div>

          {scheduleNext && (
            <div className="rounded-lg border border-sky-100 bg-sky-50/60 p-3 space-y-3"
                 data-testid="schedule-next-block">
              <div className="text-sm font-medium text-slate-800 inline-flex items-center gap-1.5">
                <CalendarClock size={14} className="text-sky-600" /> Schedule the next follow-up
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Next follow-up date</Label>
                <Input type="date" value={nextDate}
                       onChange={(e) => setNextDate(e.target.value)}
                       data-testid="schedule-next-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Notes for next follow-up (optional)</Label>
                <Textarea rows={2} value={nextNote}
                          onChange={(e) => setNextNote(e.target.value)}
                          placeholder="e.g., Call back to confirm crown fitting"
                          data-testid="schedule-next-note-input" />
              </div>
              <p className="text-xs text-slate-500">
                A new open follow-up will appear for {entry?.patient_name} on the picked date.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="close-cancel-button">Cancel</Button>
          <Button type="button" disabled={saving} onClick={submit}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="close-confirm-button">
            {saving ? "Closing..." : (scheduleNext ? "Close & schedule next" : "Close follow-up")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
